"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import {
    Loader2,
    CheckCircle2,
    AlertTriangle,
    X,
    ArrowRight,
    Sparkles,
} from "lucide-react";
import { useAuth } from "@/providers/auth-provider";
import { useWikiGenerationStore } from "@/stores/wiki-generation-store";
import { useWikiStore } from "@/stores/wiki-store";
import { refreshWorld } from "@/stores/wiki-data";
import type { WikiGenerationJobView } from "@/lib/actions/wiki/wiki-generation-types";

const POLL_INTERVAL_MS = 2500;

function isActive(job: WikiGenerationJobView): boolean {
    return job.status === "queued" || job.status === "running";
}

function progressLine(job: WikiGenerationJobView): string {
    const s = job.stats;
    const changedPages =
        (s?.entriesAdded ?? 0) + (s?.entriesRefreshed ?? 0);
    if (!s || (s.categoriesAdded === 0 && changedPages === 0)) {
        return "Reading chapters…";
    }
    const parts: string[] = [];
    if (s.entriesAdded > 0) {
        parts.push(`${s.entriesAdded} new ${s.entriesAdded === 1 ? "page" : "pages"}`);
    }
    if ((s.entriesRefreshed ?? 0) > 0) {
        parts.push(
            `${s.entriesRefreshed} refreshed ${s.entriesRefreshed === 1 ? "page" : "pages"}`
        );
    }
    if (s.categoriesAdded > 0) {
        parts.push(
            `${s.categoriesAdded} ${s.categoriesAdded === 1 ? "category" : "categories"}`
        );
    }
    if (s.connectionsAdded > 0) {
        parts.push(
            `${s.connectionsAdded} ${s.connectionsAdded === 1 ? "connection" : "connections"}`
        );
    }
    return parts.length ? `${parts.join(", ")} so far…` : "Extracting pages…";
}

function summaryLine(job: WikiGenerationJobView): string {
    const s = job.stats;
    if (!s) return "Your Wiki is ready.";
    const sentences: string[] = [];
    if ((s.entriesRefreshed ?? 0) > 0) {
        sentences.push(
            `Refreshed ${s.entriesRefreshed} generated ${s.entriesRefreshed === 1 ? "page" : "pages"}.`
        );
    }
    if (s.entriesAdded > 0) {
        sentences.push(
            `Added ${s.entriesAdded} generated ${s.entriesAdded === 1 ? "page" : "pages"}.`
        );
    }
    if (s.categoriesAdded > 0 || s.connectionsAdded > 0) {
        sentences.push(
            `Added ${s.categoriesAdded} ${s.categoriesAdded === 1 ? "category" : "categories"} and ${s.connectionsAdded} ${s.connectionsAdded === 1 ? "connection" : "connections"}.`
        );
    }
    if ((s.entriesPreserved ?? 0) > 0) {
        sentences.push(
            `Preserved ${s.entriesPreserved} ${s.entriesPreserved === 1 ? "page" : "pages"} because you edited ${s.entriesPreserved === 1 ? "it" : "them"}. For AI help updating ${s.entriesPreserved === 1 ? "that page" : "those pages"}, use the Writing Assistant.`
        );
    } else if ((s.skippedDuplicates ?? 0) > 0) {
        sentences.push(`Kept ${s.skippedDuplicates} existing pages.`);
    }
    return sentences.join(" ") || "Your Wiki is up to date.";
}

export function WikiGenerationTray() {
    const { user } = useAuth();
    const router = useRouter();
    const jobs = useWikiGenerationStore((s) => s.jobs);
    const refresh = useWikiGenerationStore((s) => s.refresh);
    const dismiss = useWikiGenerationStore((s) => s.dismiss);
    const openWorldId = useWikiStore((s) => s.wikiId);

    // Initial load once we know there's a signed-in user.
    useEffect(() => {
        if (!user) return;
        refresh();
    }, [user, refresh]);

    // Poll while any job is in flight.
    const hasActive = jobs.some(isActive);
    useEffect(() => {
        if (!user || !hasActive) return;
        const id = setInterval(refresh, POLL_INTERVAL_MS);
        return () => clearInterval(id);
    }, [user, hasActive, refresh]);

    // When a job finishes for the world currently open on screen, reload its
    // canvas in place so the new entries appear without a manual refresh.
    const handledCompletions = useRef<Set<string>>(new Set());
    useEffect(() => {
        for (const job of jobs) {
            if (job.status !== "succeeded") continue;
            if (handledCompletions.current.has(job.id)) continue;
            handledCompletions.current.add(job.id);
            if (useWikiStore.getState().wikiId === job.wikiId) {
                refreshWorld();
                router.refresh();
            }
        }
    }, [jobs, router]);

    if (!user || jobs.length === 0) return null;

    return (
        <>
            {jobs.map((job) => (
                <div
                    key={job.id}
                    className="rounded-xl border border-border bg-card/95 p-3 shadow-lg backdrop-blur-md"
                >
                    <div className="flex items-start gap-2.5">
                        <div className="mt-0.5 shrink-0">
                            {job.status === "succeeded" ? (
                                <CheckCircle2 className="h-4 w-4 text-primary" aria-hidden="true" />
                            ) : job.status === "failed" ? (
                                <AlertTriangle className="h-4 w-4 text-destructive" aria-hidden="true" />
                            ) : (
                                <Loader2 className="h-4 w-4 animate-spin text-primary" aria-hidden="true" />
                            )}
                        </div>

                        <div className="min-w-0 flex-1">
                            <p className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                                <Sparkles className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
                                {job.status === "succeeded"
                                    ? "Wiki generated"
                                    : job.status === "failed"
                                      ? "Generation failed"
                                      : "Generating Wiki"}
                            </p>
                            <p className="mt-0.5 text-xs text-muted-foreground">
                                {job.status === "succeeded"
                                    ? summaryLine(job)
                                    : job.status === "failed"
                                      ? (job.error ?? "Something went wrong. Please try again.")
                                      : progressLine(job)}
                            </p>

                            {job.status === "succeeded" &&
                                openWorldId !== job.wikiId && (
                                    <button
                                        onClick={() => router.push(`/wiki/${job.wikiId}`)}
                                        className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-primary transition-colors hover:underline"
                                    >
                                        View world
                                        <ArrowRight className="h-3 w-3" aria-hidden="true" />
                                    </button>
                                )}
                        </div>

                        {!isActive(job) && (
                            <button
                                onClick={() => dismiss(job.id)}
                                aria-label="Dismiss"
                                className="shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                            >
                                <X className="h-3.5 w-3.5" aria-hidden="true" />
                            </button>
                        )}
                    </div>
                </div>
            ))}
        </>
    );
}
